import React from 'react';
import { Target, Eye, Award, TrendingUp, Users, Globe } from 'lucide-react';
import './AboutPage.css';

const AboutPage = () => {
  const companyName = process.env.REACT_APP_COMPANY_NAME;

  const values = [
    {
      icon: <Target size={32} />,
      title: 'Excellence',
      description: 'Commitment to operational excellence and continuous improvement in all aspects'
    },
    {
      icon: <Users size={32} />,
      title: 'Safety First',
      description: 'Prioritizing the safety and wellbeing of our people, communities, and environment'
    },
    {
      icon: <Award size={32} />,
      title: 'Integrity',
      description: 'Conducting business with the highest ethical standards and transparency'
    },
    {
      icon: <TrendingUp size={32} />,
      title: 'Innovation',
      description: 'Embracing technology and innovation to drive efficiency and sustainability'
    },
    {
      icon: <Globe size={32} />,
      title: 'Sustainability',
      description: 'Responsible resource management for a sustainable energy future'
    },
    {
      icon: <Users size={32} />,
      title: 'Collaboration',
      description: 'Building strong partnerships with stakeholders and communities'
    }
  ];

  const milestones = [
    { year: '2015', event: 'Company founded with first exploration project' },
    { year: '2017', event: 'First major refinery commissioned' },
    { year: '2019', event: 'Expanded operations to offshore platforms' },
    { year: '2021', event: 'Achieved ISO 14001 environmental certification' },
    { year: '2022', event: 'Invested $500M in renewable energy initiatives' },
    { year: '2025', event: 'Reached 2.5 million barrels per day capacity' }
  ];

  const certifications = [
    'ISO 9001:2015 - Quality Management',
    'ISO 14001:2016 - Environmental Management',
    'ISO 45001:2018 - Occupational Health & Safety',
    'API Q1 & Q2 Certifications',
    'EPA Compliance & Environmental Permits',
    'OSHA VPP Star Site Recognition'
  ];

  return (
    <div className="about-page">
      <section className="about-hero">
        <div className="about-hero-overlay"></div>
        <div className="about-hero-content">
          <h1>About Us</h1>
          <p>Building a sustainable energy future through operational excellence and innovation</p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="about-intro-grid">
            <div className="about-intro-image">
              <img
                src="https://images.unsplash.com/photo-1697289934707-c139aa38f94c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3MjQyMTd8MHwxfHNlYXJjaHwxfHxvaWwlMjB3b3JrZXJzJTIwc2FmZXR5fGVufDB8fHx8MTc2NTEzMzM2MXww&ixlib=rb-4.1.0&q=85"
                alt="Our Team"
              />
            </div>
            <div className="about-intro-content">
              <h2>Our Story</h2>
              <p>
                Since our incorporation date on April 22 2015, {companyName} has grown from a regional exploration company into a leading integrated energy provider. Our journey has been defined by strategic vision, technological innovation, and an unwavering commitment to safety and environmental stewardship.
              </p>
              <p>
                Today, we operate world-class refineries, offshore platforms, and an extensive distribution network that delivers energy solutions to millions of customers worldwide. Our success is built on the expertise of our 1,000+ professionals who bring dedication and excellence to every project.
              </p>
              <p>
                As the energy landscape evolves, we remain committed to responsible resource development while investing in sustainable technologies that will power the future.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="mission-vision-section">
        <div className="container">
          <div className="mission-vision-grid">
            <div className="mission-card">
              <div className="mv-icon">
                <Target size={48} />
              </div>
              <h3>Our Mission</h3>
              <p>
                To safely and responsibly explore, refine, and deliver energy solutions that power communities and industries while minimizing environmental impact and maximizing value for stakeholders.
              </p>
            </div>
            <div className="vision-card">
              <div className="mv-icon">
                <Eye size={48} />
              </div>
              <h3>Our Vision</h3>
              <p>
                To be the most trusted and sustainable energy partner, recognized globally for operational excellence, innovation, and our commitment to building a cleaner energy future.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="values-section section">
        <div className="container">
          <h2 className="section-title">Our Core Values</h2>
          <p className="section-subtitle">
            The principles that guide every decision and action we take
          </p>
          <div className="values-grid">
            {values.map((value, index) => (
              <div key={index} className="value-card">
                <div className="value-icon">{value.icon}</div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="timeline-section">
        <div className="container">
          <h2 className="section-title">Our Journey</h2>
          <p className="section-subtitle">
            Key milestones that shaped our growth and success
          </p>
          <div className="timeline">
            {milestones.map((milestone, index) => (
              <div key={index} className="timeline-item">
                <div className="timeline-marker"></div>
                <div className="timeline-content">
                  <span className="timeline-year">{milestone.year}</span>
                  <p>{milestone.event}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="certifications-section section">
        <div className="container">
          <div className="certifications-grid">
            <div className="certifications-content">
              <h2>Certifications & Compliance</h2>
              <p>
                Our commitment to quality, safety, and environmental responsibility is validated by industry-leading certifications and compliance frameworks.
              </p>
              <ul className="certifications-list">
                {certifications.map((cert, index) => (
                  <li key={index}>
                    <Award size={20} />
                    <span>{cert}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="certifications-image">
              <img
                src="https://images.pexels.com/photos/7932921/pexels-photo-7932921.jpeg"
                alt="Safety and Compliance"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="leadership-section section">
        <div className="container">
          <h2 className="section-title">Leadership & Management</h2>
          <p className="section-subtitle">
            Experienced leaders driving our strategic vision and operational excellence
          </p>
          <div className="leadership-note">
            <p>
              Our leadership team brings decades of combined experience in energy exploration, refining operations, and sustainable business practices. Guided by our values and driven by innovation, they lead {companyName} toward a future of continued growth and environmental responsibility.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;